import { Component, Inject, OnInit } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog'
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { NgClass } from '@angular/common'
import { AutoCompleteModule } from 'primeng/autocomplete'
import { ItemService } from '../item-list/item.service'
import { SpinnerOverlayService } from '../../../Components/spinner/spinner-overlay.service'
import { CustomerService } from '../../../shared/service/customer-service';
import { RoleService } from '../../../shared/service/role-service';
import { IItem } from '../item-list/item.model';
import { PrimeNG } from 'primeng/config';
import { MessageService } from '../../../shared/message.service';

@Component({
  selector: 'app-item-update',
  imports: [
    MatDialogContent,
    MatDialogActions,
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    AutoCompleteModule,
],
  templateUrl: './item-update.component.html',
  styleUrl: './item-update.component.css'
})
export class ItemUpdateComponent implements OnInit {
  form!: FormGroup
  item: IItem | null = null
  files = [];
  totalSize: number = 0;
  totalSizePercent: number = 0;

  constructor(
    private config: PrimeNG, private spinner: SpinnerOverlayService,
    private fb: FormBuilder, public dialogRef: MatDialogRef<ItemUpdateComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private itemService: ItemService,
    private customerService: CustomerService,
    private roleService: RoleService,
    private messageService: MessageService
  ) {
  }

  ngOnInit() {
    this.form = this.fb.group({
      id: [{value: this.item?.id ?? '', disabled: true}, [Validators.required]],
      category: [{value: this.item?.product?.category ?? '', disabled: true}, [Validators.required]],
      productType: [{value: this.item?.product?.productType ?? '', disabled: true}, [Validators.required]],
      finish: [{value: this.item?.product?.finish ?? '', disabled: true}, [Validators.required]],
      description: [{value: this.item?.description ?? '', disabled: true}, [Validators.required]],
      dimensions: [{value: this.item?.dimensions ?? '', disabled: true}, [Validators.required]],
      color: [{value: this.item?.color?.name ?? '', disabled: true}, [Validators.required]],
      texture: [{value: this.item?.texture ?? '', disabled: true}, [Validators.required]],
      thickness: [{value: this.item?.thickness ? this.item?.thickness + ' mm' : '', disabled: true}, [Validators.required]],
      platform: [this.item?.platform ?? '', [Validators.pattern('^[0-9]+$')]],
      halfPlatform: [this.item?.halfPlatform ?? '', [Validators.pattern('^[0-9]+$')]],
      quarterPlatform: [this.item?.quarterPlatform ?? '', [Validators.pattern('^[0-9]+$')]],
      eighthPlatform: [this.item?.eighthPlatform ?? '', [Validators.pattern('^[0-9]+$')]],
      leadTime: [this.item?.leadTime ?? '', [Validators.pattern('^[0-9]+$')]],
    })

    if (this.data) {
      this.findById()
    }
  }

  async patchFormProduct() {
      
      if (!this.item) return;
  
      this.form.patchValue({
        id: this.item?.id,
        category: this.item?.product?.category,
        productType: this.item?.product?.productType,
        finish: this.item?.product?.finish,
        description: this.item?.description,
        dimensions: this.item?.dimensions,
        color: this.item?.color?.name,
        texture: this.item?.texture,
        thickness: this.item?.thickness ? this.item?.thickness + ' mm' : '',
        platform: this.item?.platform ?? '',
        halfPlatform: this.item?.halfPlatform ?? '',
        quarterPlatform: this.item?.quarterPlatform ?? '',
        eighthPlatform: this.item?.eighthPlatform ?? '',
        leadTime: this.item?.leadTime ?? '',
      });
  
  }

  findById() {
    this.handleLoading(true)

    this.itemService.findById(this.data.id).subscribe({
      next: (response) => {
        this.item = response.body?.item ?? null
        this.patchFormProduct()
        this.handleLoading(false)
      },
      error: () => {
        this.handleLoading(false)
      }
    })
  }

  close(save: boolean): void {
    this.dialogRef.close(save)
  }

  get f() {
    return this.form.controls
  }

  get colorArray(): FormArray {
    return this.form.get('color') as FormArray;
  }

  onRemoveTemplatingFile(event: any, file: {
    size: any
  }, removeFileCallback: (arg0: any, arg1: any) => void, index: any) {
    removeFileCallback(event, index);
    this.totalSize -= parseInt(this.formatSize(file.size));
    this.totalSizePercent = this.totalSize / 10;
    const control = this.form.get('images');
    if (!control) return;

    const updatedFiles = [...(control.value || [])];
    updatedFiles.splice(index, 1);

    control.patchValue(updatedFiles);
    control.markAsDirty();
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  formatSize(bytes: number) {
    const k = 1024;
    const dm = 3;
    const sizes = this.config.translation.fileSizeTypes;
    if (bytes === 0) {
      return `0 ${sizes ? sizes : [0]}`;
    }

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

    return `${formattedSize} ${sizes ? sizes : [i]}`;
  }

  update() {
    this.handleLoading(true);

    const formValue = this.form.value;

    const payload: any = {
      id: formValue.id,
      platform: formValue.platform,
      halfPlatform: formValue.halfPlatform,
      quarterPlatform: formValue.quarterPlatform,
      eighthPlatform: formValue.eighthPlatform,
      leadTime: formValue.leadTime,
    }
 
    this.itemService.update(payload, this.data.id).subscribe({
      next: () => {
        this.handleLoading(false);
        this.close(true);
      },
      error: () => {
        this.handleLoading(false);
      }
    });
  }

  handleLoading(loading: boolean) {
    if (loading) this.spinner.show(); else this.spinner.hide()
  }

  choose(event: any, callback: any) {
    callback();
  }

  getBackgroundClasses(active: boolean) {
    if (active) return 'bg-green-100';
    return 'bg-red-100';
  }

  onlyDigits(event: KeyboardEvent): void {
    const key = event.key;
    if (
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'Tab' ||
      key === 'Escape' ||
      key === 'Enter' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'ArrowUp' ||
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End'
    ) {
      return;
    }
    if ((event.ctrlKey || event.metaKey) && (key === 'a' || key === 'c' || key === 'v' || key === 'x')) {
      return;
    }
    if (!/^\d$/.test(key)) {
      event.preventDefault();
    }
  }

  onPaste(event: ClipboardEvent): void {
    const pastedText = event.clipboardData?.getData('text');
    if (pastedText && !/^\d+$/.test(pastedText)) {
      event.preventDefault();
    }
  }
}
